import React, { useEffect } from "react";

import { Button, Flex, Form, Input, Typography } from "antd";

import { useTranslation } from "react-i18next";

import { useSelector } from "react-redux";

import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { makeSelectLoginFormErrors } from "../../../../selectors";

import { LoginFormProps, LoginFormInputModel } from "./types";

function LoginForm({ loadingData, onLogin, onOpenRestorePasswordView, onOpenRegisterView }: LoginFormProps) {
  const { t } = useTranslation("translations");
  const [form] = Form.useForm<LoginFormInputModel>();

  const formErrors = useSelector(makeSelectLoginFormErrors());

  useEffect(() => {
    if (formErrors) {
      const errors: Parameters<typeof form.setFields>[0] = [];

      if (formErrors.find((x) => x.field === "login" && x.errorCode === "2")) {
        errors.push({
          name: "login",
          errors: [t("user_with_such_identification_number_not_found")],
        });
      }

      if (formErrors.find((x) => x.field === "password" && x.errorCode === "4")) {
        errors.push({
          name: "password",
          errors: [t("wrong_password")],
        });
      }

      form.setFields(errors);
    }
  }, [formErrors, form, t]);

  const onFinish = ({ login, password }: LoginFormInputModel) => {
    onLogin({ login, password });
  };

  return (
    <Form<LoginFormInputModel> onFinish={onFinish} form={form} layout="vertical" requiredMark={false} preserve={false}>
      <Form.Item
        name="login"
        label={t("identification_number")}
        normalize={(value: string) => value.replace(/\D/g, "").slice(0, 12)}
        rules={[
          () => ({
            validator(_, value: string) {
              if (value && value.length >= 10 && value.length <= 12) {
                return Promise.resolve();
              }
              if (!value) {
                return Promise.reject(t("enter_identification_number"));
              }

              return Promise.reject(t("identification_number_length"));
            },
          }),
        ]}
      >
        <Input
          prefix={<UserOutlined aria-hidden />}
          inputMode="numeric"
          maxLength={12}
          autoComplete="username"
          aria-label={t("identification_number")}
        />
      </Form.Item>
      <Form.Item name="password" label={t("password")} rules={[{ required: true, message: t("enter_password")! }]}>
        <Input.Password prefix={<LockOutlined aria-hidden />} autoComplete="current-password" aria-label={t("password")} />
      </Form.Item>
      <Form.Item>
        <Button type="link" htmlType="button" onClick={onOpenRestorePasswordView} style={{ paddingInline: 0 }}>
          {t("forgot_password")}
        </Button>
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit" block loading={loadingData}>
          {t("enter")}
        </Button>
      </Form.Item>

      <Flex align="center" justify="center" gap="small" wrap>
        <Typography.Text>{t("is_new_user")}</Typography.Text>
        <Button onClick={onOpenRegisterView} type="link" htmlType="button">
          {t("register")}
        </Button>
      </Flex>
    </Form>
  );
}

export default React.memo(LoginForm);
